import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc } from '@goaltree/backend/convex/_generated/dataModel'
import {
  COACH_CHARACTERS,
  COACH_STYLES,
  COACH_VOICES,
  CUSTOM_VOICE,
  allowedCoachStyle,
  coachCharacter,
  coachVoiceId,
  coachVoiceLabel,
  isCoachPersona,
  isFallingBehind,
  realisticCoach,
  realisticCoachesFor,
  realisticVoice,
  resolveDynamicCoachStyle,
} from '@goaltree/backend/convex/lib/coach'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { CheckIcon, ChevronDownIcon, LockIcon, MicIcon, Volume2Icon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CoachAvatar } from '@/components/coach-avatar'
import { CoachChangeLog } from '@/components/coach-change-log'
import { CoachCharacterList } from '@/components/coach-characters'
import { CoachLookToggle } from '@/components/coach-look-toggle'
import { CoachNameField } from '@/components/coach-name-field'
import { CoachVoiceRecorder } from '@/components/coach-voice-recorder'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useCoachIdentity } from '@/hooks/use-coach-name'
import { useGoals } from '@/hooks/use-goals'
import { useIntroPreview } from '@/hooks/use-intro-preview'
import { useIsAdmin } from '@/hooks/use-is-admin'
import { useIsPro } from '@/hooks/use-is-pro'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { optimisticClearCustomVoice } from '@/lib/prefs-optimistic'

import { CheckinSchedule } from './checkin-schedule'
import { SettingsGroup, SettingsRow, SettingsSection, Toggle, rowId } from './primitives'

type Prefs = Partial<Doc<'userPreferences'>>

export function useCoachName() {
  const { loaded, name } = useCoachIdentity()
  return loaded ? name : undefined
}

const link =
  'inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs font-semibold text-primary-text hover:underline'

export function CoachSection() {
  const { t, i18n } = useTranslation()
  const { isPro, isLoading: proLoading } = useIsPro()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  const updatePrefs = useUpdatePreferences()
  const clearCustomVoice = useMutation(api.preferences.clearCustomVoice).withOptimisticUpdate(
    optimisticClearCustomVoice,
  )
  const streakInfo = useQuery(api.nodes.getUserStreakInfo)
  const goals = useGoals()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [recorderOpen, setRecorderOpen] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const isAdmin = useIsAdmin()
  const coachVoice = prefs?.coachVoice
  const avatarStyle =
    isPro && (prefs as { coachAvatarStyle?: string } | undefined)?.coachAvatarStyle === 'realistic'
      ? 'realistic'
      : 'illustrated'
  const custom = coachVoice === CUSTOM_VOICE
  const { name: coachName, customName } = useCoachIdentity()
  const pinnedId = allowedCoachStyle(prefs?.coachStyle, isPro || proLoading)
  const pinnedStyle = isCoachPersona(pinnedId) ? pinnedId : undefined
  const active = goals?.filter((g) => !g.isInbox && g.status !== 'done') ?? []
  const liveStyle = resolveDynamicCoachStyle({
    lifestyle: prefs?.lifestyle,
    behind: isFallingBehind(
      active.map((g) => ({ progress: g.progress, remaining: g.remainingCount })),
      streakInfo?.streak ?? 0,
    ),
  })
  const intro = useIntroPreview(pinnedStyle ?? liveStyle)
  // Realistic look = vaboo's cast; illustrated = the drawn coaches (+ your clone).
  const realistic = avatarStyle === 'realistic' && !!realisticCoach(coachVoice)
  const voice = realistic
    ? { id: coachVoice!, label: coachVoiceLabel(coachVoice) }
    : COACH_VOICES.find((v) => v.id === coachVoiceId(coachVoice))!
  const styleName = (id: string) => t(`coach.style.${id}`)
  const voices =
    avatarStyle === 'realistic'
      ? realisticCoachesFor(isAdmin).map((c) => ({
          id: realisticVoice(c.id),
          label: c.name,
        }))
      : [
          ...(prefs?.customVoiceId ? [{ id: CUSTOM_VOICE, label: t('coach.customVoice') }] : []),
          ...COACH_VOICES,
        ]

  useEffect(() => {
    if (pickerOpen) intro.prefetch(voices.filter((v) => v.id !== CUSTOM_VOICE).map((v) => v.id))
    else intro.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, i18n.language])

  useEffect(() => {
    if (coachVoice && !custom) for (const c of COACH_CHARACTERS) intro.prefetch([coachVoice], c.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachVoice, i18n.language])

  // Picking a character introduces your coach in character (after the pin lands,
  // so the clip is fetched with the new style).
  const prevPinned = useRef(pinnedStyle)
  useEffect(() => {
    const prev = prevPinned.current
    prevPinned.current = pinnedStyle
    if (pinnedStyle !== prev && coachCharacter(pinnedStyle) && coachVoice && !custom)
      void intro.play(coachVoice)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedStyle])

  function pickVoice(id: string) {
    void updatePrefs({ coachVoice: id })
    if (id !== CUSTOM_VOICE) void intro.play(id)
  }

  return (
    <SettingsSection id="coach" title={t('settings.sec.coach')}>
      <CoachVoiceRecorder
        open={recorderOpen}
        onOpenChange={setRecorderOpen}
        onCloned={() => setRecorderOpen(false)}
      />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="voice" />
      <SettingsGroup>
        <div id={rowId('coach.voice')} className="flex scroll-mt-24 items-center gap-3 px-4 py-3">
          <button
            type="button"
            disabled={custom}
            onClick={() => intro.toggle(voice.id)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left disabled:cursor-default"
          >
            <CoachAvatar voice={coachVoice} size={36} speaking={!!intro.playing} />
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{coachName}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                {!custom && <Volume2Icon className="h-3 w-3" />}
                {custom
                  ? t('settings.blurb.custom')
                  : realistic
                    ? t('settings.tapToHear', {
                        blurb: t(`coach.castBlurb.${realisticCoach(coachVoice)!.id}`, {
                          defaultValue: realisticCoach(coachVoice)!.blurb,
                        }),
                      })
                    : t('settings.tapToHear', { blurb: t(`settings.blurb.${voice.id}`) })}
              </span>
            </span>
          </button>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger className={link}>{t('settings.change')} ›</PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-3">
              <p className="mb-2 flex justify-between text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
                {t('settings.chooseVoice')}
                <span className="font-medium normal-case tracking-normal">
                  {t('coach.tapToHear')}
                </span>
              </p>
              <div className="grid grid-cols-5 gap-2">
                {voices.map((v) => {
                  const on =
                    v.id === CUSTOM_VOICE
                      ? custom
                      : realisticCoach(v.id)
                        ? coachVoice === v.id
                        : !custom &&
                          !realisticCoach(coachVoice) &&
                          coachVoiceId(coachVoice) === v.id
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => pickVoice(v.id)}
                      className={cn(
                        'flex cursor-pointer flex-col items-center gap-1 text-[10.5px]',
                        on ? 'font-bold text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'flex rounded-full',
                          on && 'ring-2 ring-primary ring-offset-2 ring-offset-popover',
                        )}
                      >
                        <CoachAvatar voice={v.id} size={40} speaking={intro.playing === v.id} />
                      </span>
                      <span className="max-w-full truncate">
                        {on && customName ? customName : v.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div id={rowId('coach.nameField.label')} className="scroll-mt-24 px-4 py-3">
          <p className="text-sm font-medium">{t('coach.nameField.label')}</p>
          <p className="mb-2 text-xs text-muted-foreground">{t('coach.nameField.hint')}</p>
          <CoachNameField />
        </div>

        <SettingsRow
          id={rowId('coach.tone')}
          label={t('coach.personality')}
          description={
            pinnedStyle
              ? styleName(pinnedStyle)
              : t('settings.toneAdaptiveRec', {
                  style: styleName(liveStyle).toLocaleLowerCase(i18n.language),
                })
          }
          example={t(`coach.toneHint.${pinnedStyle ?? 'adaptive'}`)}
        >
          <DropdownMenu>
            <DropdownMenuTrigger className={link}>
              {pinnedStyle ? styleName(pinnedStyle) : `✦ ${t('coach.adaptive')}`}
              <ChevronDownIcon className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => {
                  void updatePrefs({ coachStyle: null })
                  if (!custom) void intro.play(voice.id)
                }}
              >
                ✦ {t('coach.adaptive')}
                {!pinnedStyle && <CheckIcon className="ml-auto h-3.5 w-3.5" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel>{t('coach.everyday')}</DropdownMenuLabel>
                {COACH_STYLES.map(({ id }) => (
                  <DropdownMenuItem
                    key={id}
                    onClick={() => {
                      void updatePrefs({ coachStyle: id })
                      if (!custom) void intro.play(voice.id)
                    }}
                  >
                    {styleName(id)}
                    {pinnedStyle === id && <CheckIcon className="ml-auto h-3.5 w-3.5" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel>{t('coach.characters')}</DropdownMenuLabel>
                {COACH_CHARACTERS.map((c) => {
                  const locked = c.pro && !isPro
                  return (
                    <DropdownMenuItem
                      key={c.id}
                      onClick={() =>
                        locked ? setUpgradeOpen(true) : void updatePrefs({ coachStyle: c.id })
                      }
                    >
                      {styleName(c.id)}
                      {pinnedStyle === c.id ? (
                        <CheckIcon className="ml-auto h-3.5 w-3.5" />
                      ) : (
                        locked && <LockIcon className="ml-auto h-3 w-3 text-muted-foreground" />
                      )}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SettingsRow>
        <div id={rowId('coach.characters')} className="scroll-mt-24 px-2 py-3">
          <p className="px-2 text-sm font-medium">{t('coach.characters')}</p>
          <p className="mb-1 px-2 text-xs text-muted-foreground">{t('coach.personalityHint')}</p>
          <CoachCharacterList
            pinned={pinnedStyle}
            onPick={(id) => void updatePrefs({ coachStyle: id })}
            onLocked={() => setUpgradeOpen(true)}
          />
        </div>
        <SettingsRow id={rowId('coach.readAloud')} label={t('coach.readAloud')}>
          <Toggle
            label={t('coach.readAloud')}
            checked={prefs?.coachAutoVoice !== false}
            onChange={(v) => void updatePrefs({ coachAutoVoice: v })}
            disabled={!prefs}
          />
        </SettingsRow>
        <SettingsRow
          id={rowId('coach.avatarStyle')}
          label={t('coach.avatarStyle')}
          description={t('coach.avatarStyleDesc')}
        >
          <CoachLookToggle onLocked={() => setUpgradeOpen(true)} />
        </SettingsRow>
        <SettingsRow
          id={rowId('coachCall.setting')}
          label={t('coachCall.setting', { name: coachName })}
          description={t('coachCall.settingDesc', { name: coachName })}
        >
          <Toggle
            label={t('coachCall.setting', { name: coachName })}
            checked={isPro && !!(prefs as { coachCallsOn?: boolean } | undefined)?.coachCallsOn}
            onChange={(v) => {
              if (v && !isPro) {
                setUpgradeOpen(true)
                return
              }
              void updatePrefs({ coachCallsOn: v })
            }}
            disabled={!prefs}
          />
        </SettingsRow>
        <CheckinSchedule
          schedule={prefs?.coachCheckins}
          isPro={isPro}
          coachName={coachName}
          onLocked={() => setUpgradeOpen(true)}
        />
        <div id={rowId('coachReach.label')} className="scroll-mt-24 px-4 py-3.5">
          <p className="text-sm font-medium">{t('coachReach.label', { name: coachName })}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('coachReach.desc')}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(['off', 'less', 'normal', 'more'] as const).map((id) => {
              const active = (prefs?.coachReach ?? 'normal') === id
              return (
                <button
                  key={id}
                  type="button"
                  disabled={!prefs}
                  onClick={() => void updatePrefs({ coachReach: id })}
                  className={cn(
                    'cursor-pointer rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50',
                    active
                      ? 'bg-primary/15 text-primary-text'
                      : 'bg-muted text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`coachReach.${id}`)}
                </button>
              )
            })}
          </div>
        </div>
        <SettingsRow
          id={rowId('coachShow.label')}
          label={t('coachShow.label', { name: coachName })}
          description={t('coachShow.desc', { name: coachName })}
        >
          <Toggle
            label={t('coachShow.label', { name: coachName })}
            checked={prefs?.coachShowInApp !== false}
            onChange={(v) => void updatePrefs({ coachShowInApp: v })}
            disabled={!prefs}
          />
        </SettingsRow>
        <SettingsRow
          id={rowId('coachSee.label')}
          label={t('coachSee.label', { name: coachName })}
          description={t('coachSee.desc', { name: coachName })}
        >
          <Toggle
            label={t('coachSee.label', { name: coachName })}
            checked={prefs?.coachSeeScreen !== false}
            onChange={(v) => void updatePrefs({ coachSeeScreen: v })}
            disabled={!prefs}
          />
        </SettingsRow>
        <CoachChangeLog name={coachName} />

        <SettingsRow
          id={rowId('settings.clonedVoice')}
          label={t('settings.clonedVoice')}
          description={
            prefs?.customVoiceId ? t('settings.clonedVoiceOn') : t('settings.clonedVoiceDesc')
          }
        >
          <div className="flex items-center gap-3">
            {prefs?.customVoiceId && (
              <button
                type="button"
                onClick={() => void clearCustomVoice({})}
                className="cursor-pointer text-xs text-muted-foreground hover:text-destructive"
              >
                {t('coach.removeVoice')}
              </button>
            )}
            <button
              type="button"
              onClick={() => (isPro ? setRecorderOpen(true) : setUpgradeOpen(true))}
              className={link}
            >
              <MicIcon className="h-3.5 w-3.5" />
              {prefs?.customVoiceId ? t('coach.rerecord') : `${t('settings.record')} ›`}
            </button>
          </div>
        </SettingsRow>
      </SettingsGroup>
    </SettingsSection>
  )
}
